import {Record, List} from 'immutable';
import type {RecordOf} from 'immutable';
import {GsapPluginKey} from '@packages/systems/gsap-integration-settings';
import type {CodeComponentsSafeModeReason} from '@packages/domains/code-libraries/data-types';

type ID = string;
type URL = string;

type SiteProps = {
  id: ID;
  base: string | null;
  beautifyHtml: boolean;
  bodyHtml: string;
  cdnHost: string | null | undefined;
  database: string | null;
  databaseVersion: string | null;
  faviconUrl: URL | null | undefined;
  faviconDarkUrl: URL | null | undefined;
  faviconUrl48: URL | null | undefined;
  googleAnonymizeIP: boolean;
  googleAnalyticsId: string | null | undefined;
  googleUniversalAnalyticsId: string | null | undefined;
  googleAnalyticsFourId: string | null | undefined;
  googleVerification: string | null | undefined;
  globalCanonicalTag: string | null | undefined;
  facebookPixelId: string | null | undefined;
  facebookPixelLazyLoad: boolean | null | undefined;
  headHtml: string | null | undefined;
  hideBranding: boolean;
  lang: string | null;
  lastPublished: string;
  name: string | null | undefined;
  ogDesc: string;
  ogImages: List<URL>;
  ogTitle: string;
  otherFonts: List<URL>;
  responsiveImages: boolean;
  seoDesc: string;
  seoTitle: string;
  shortName: string;
  showWebflowBadge: boolean;
  pageLinkHrefPrefix: string;
  siteId: ID;
  sslHosting: boolean;
  sslOk: boolean;
  timezone: string;
  typeKitKitId: string | null | undefined;
  webClipUrl: URL | null | undefined;
  webClipUrl180: URL | null | undefined;
  webClipUrl192: URL | null | undefined;
  recaptchaSiteKey: string | null | undefined;
  intellimizeDataCollectionEnabled: boolean;
  intellimizeDataCollectionType: string | null | undefined;
  intellimizeAntiFlickerTimeout: number | null | undefined;
  isGsapEnabled: boolean;
  gsapPluginsEnabled: GsapPluginKey[];
  codeComponentsSafeModeReason: CodeComponentsSafeModeReason | null | undefined;
};

// Contains information specific to a Webflow site

const Site = Record<SiteProps>(
  {
    id: '<id>', // ID
    base: null, // String
    beautifyHtml: false, // Boolean
    bodyHtml: '', // String
    database: null, // String
    databaseVersion: null, // String
    faviconUrl: null, // URL
    faviconDarkUrl: null, // URL
    faviconUrl48: null, // URL
    googleAnonymizeIP: false, // Boolean
    googleAnalyticsId: null, // String
    googleUniversalAnalyticsId: null, // String
    googleAnalyticsFourId: null, // String
    googleVerification: null, // String
    globalCanonicalTag: null, // String
    facebookPixelId: null, // String
    facebookPixelLazyLoad: false, // Boolean
    headHtml: '', // String
    hideBranding: false, // Boolean
    lang: null, // String
    lastPublished: '', // String
    name: null, // String
    ogDesc: '', // String
    ogImages: List(), // List URL
    ogTitle: '', // String
    otherFonts: List(), // List URL?
    responsiveImages: false, // Boolean
    seoDesc: '', // String
    seoTitle: '', // String
    shortName: '', // String
    showWebflowBadge: true, // Boolean
    pageLinkHrefPrefix: '', // String
    siteId: '<id>', // ID
    sslHosting: false, // Boolean
    timezone: 'UTC', // String
    typeKitKitId: null, // String
    webClipUrl: null, // URL
    webClipUrl180: null, // URL
    webClipUrl192: null, // URL
    cdnHost: null, // String
    sslOk: false, // Boolean
    recaptchaSiteKey: null, // String
    intellimizeDataCollectionEnabled: false, // Boolean
    intellimizeDataCollectionType: null, // String
    intellimizeAntiFlickerTimeout: null, // Number
    isGsapEnabled: false, // Boolean
    gsapPluginsEnabled: [], // Array String
    codeComponentsSafeModeReason: undefined, // String
  },
  'Site'
);

export type SiteType = RecordOf<SiteProps>;
export default Site;
